import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import type { UseFormReturn } from "react-hook-form";
import type { JobPostFormInput } from "@/components/Forms/JobPostForm/types";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { buildEstimateInputFromForm, geographyFromFacility, toRangedEstimate } from "./criteria";
import { ESTIMATE_JOB_APPLICANT_COUNT_MUTATION } from "./mutations";
import { FACILITY_ESTIMATE_GEOGRAPHY_QUERY, type JobApplicantCountEstimate } from "./queries";

export const ESTIMATE_DEBOUNCE_MS = 300;

export function useJobApplicantCountEstimate(form: UseFormReturn<JobPostFormInput>): {
  estimate: JobApplicantCountEstimate | null;
  criteriaComplete: boolean;
} {
  const facilityId = form.watch("facilityId");
  const serviceLevel = form.watch("serviceLevel");
  const category = form.watch("category");
  const applicantGroup = form.watch("applicantGroup");
  const positionType = form.watch("positionType");
  const requiredExperienceInYears = form.watch("requiredExperienceInYears");
  const paySchedule = form.watch("paySchedule");
  const requiredEducation = form.watch("requiredEducation");
  const additionalPgaEducation = form.watch("additionalPgaEducation");
  const applyVia = form.watch("applyVia");
  const compensation = form.watch("compensation");

  const { data: facilityData, loading: geographyLoading } = useQuery(FACILITY_ESTIMATE_GEOGRAPHY_QUERY, {
    variables: { facilityId },
    skip: !facilityId,
    fetchPolicy: "cache-first",
    onError: () => {
      showErrorSnackbar("Could not load facility location for the applicant estimate.");
    }
  });

  const geography = geographyFromFacility(facilityData?.facility);
  const formValues: JobPostFormInput = {
    ...form.getValues(),
    facilityId,
    serviceLevel,
    category,
    applicantGroup,
    positionType,
    requiredExperienceInYears,
    paySchedule,
    requiredEducation,
    additionalPgaEducation,
    applyVia,
    compensation
  };
  const input = buildEstimateInputFromForm(formValues, geography, { geographyLoading });
  const criteriaComplete = input !== null;

  const [estimateJobApplicantCount] = useMutation(ESTIMATE_JOB_APPLICANT_COUNT_MUTATION, {
    onError: () => {
      showErrorSnackbar("Could not update the applicant count estimate.");
    }
  });
  const [estimate, setEstimate] = useState<JobApplicantCountEstimate | null>(null);
  const inputKey = JSON.stringify(input);

  useEffect(() => {
    if (!inputKey || inputKey === "null") {
      setEstimate(null);
      return;
    }

    const currentInput = JSON.parse(inputKey) as NonNullable<typeof input>;
    let cancelled = false;
    const run = debounce(() => {
      void estimateJobApplicantCount({ variables: { input: currentInput } })
        .then((result) => {
          if (cancelled) {
            return;
          }
          setEstimate(toRangedEstimate(result.data?.estimateJobApplicantCount));
        })
        .catch(() => {
          if (!cancelled) {
            setEstimate(null);
          }
        });
    }, ESTIMATE_DEBOUNCE_MS);

    run();

    return () => {
      cancelled = true;
      run.cancel();
    };
  }, [estimateJobApplicantCount, inputKey]);

  return { estimate, criteriaComplete };
}
